'use client';

import { type ReactNode, useLayoutEffect, useMemo, useRef } from 'react';
import DatePicker, { type DatePickerProps, registerLocale } from 'react-datepicker';

import clsx from 'clsx';
import { enUS } from 'date-fns/locale/en-US';
import { ru } from 'date-fns/locale/ru';
import dayjs, { type Dayjs } from 'dayjs';

import { type ControlState } from '@/components/common/Control';
import { DatePickerHeader } from '@/components/common/input/DateInput/components/DatePickerHeader';
import { DatePickerTimeSelect } from '@/components/common/input/DateInput/components/DatePickerTimeSelect';
import { useCombineRefs } from '@/hooks/use-combine-refs';
import { useElementSize } from '@/hooks/use-element-size';
import { useLocale } from '@/hooks/use-locale';
import { type HtmlElementRef } from '@/types/react';
import { getCssSizeValue } from '@/utils/css';
import { isDefined } from '@/utils/utils';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './DateInput.module.scss';

export type DateInputProps = Omit<DatePickerProps, 'selected'> & {
  ref?: HtmlElementRef<HTMLInputElement>;
  selected?: Date | Dayjs | null;
  variant?: 'default' | 'box';
  prefix?: ReactNode;
  suffix?: ReactNode;
  state?: ControlState;
};

registerLocale('en', enUS);
registerLocale('ru', ru);

export const DateInput = (props: DateInputProps) => {
  const {
    className,
    popperClassName,
    wrapperClassName,
    calendarClassName,
    weekDayClassName,
    weekClassName,
    dayClassName,
    ref,
    variant = 'default',
    prefix,
    suffix,
    state = 'default',
    selected,
    showTimeInput = true,
    ...datepickerProps
  } = props;

  const { size: prefixSize, ref: prefixRef } = useElementSize();
  const { size: suffixSize, ref: suffixRef } = useElementSize();

  const rootRef = useRef<HTMLDivElement | null>(null);
  const combinedInputRef = useCombineRefs([ref]);

  const locale = useLocale();

  const value = useMemo(() => {
    if (dayjs.isDayjs(selected)) {
      return selected.toDate();
    }

    return selected;
  }, [selected]);

  useLayoutEffect(() => {
    const input = rootRef.current?.querySelector('input');

    combinedInputRef(input ?? null);

    return () => {
      combinedInputRef(null);
    };
  }, [combinedInputRef]);

  return (
    <div
      className={clsx(
        styles.root,
        styles[`variant_${variant}`],
        styles[`state_${state}`],
        {
          [styles.withPrefix]: prefix,
          [styles.withSuffix]: suffix,
        },
        className
      )}
      style={{
        '--date-input-prefix-width': getCssSizeValue(prefixSize.width),
        '--date-input-suffix-width': getCssSizeValue(suffixSize.width),
      }}
    >
      <DatePicker
        className={styles.input}
        wrapperClassName={clsx(styles.wrapper, wrapperClassName)}
        popperClassName={clsx(styles.popper, popperClassName)}
        calendarClassName={clsx(styles.calendar, calendarClassName)}
        weekDayClassName={(...args) => clsx(styles.weekday, weekDayClassName?.(...args))}
        weekClassName={(...args) => clsx(styles.week, weekClassName?.(...args))}
        dayClassName={(...args) => clsx(styles.day, dayClassName?.(...args))}
        selected={value}
        popperPlacement="bottom-start"
        showPopperArrow={false}
        showTimeInput={showTimeInput}
        showTimeCaption={false}
        shouldCloseOnSelect={false}
        dateFormat={showTimeInput ? 'dd.MM.yyyy HH:mm:ss' : 'dd.MM.yyyy'}
        timeFormat="HH:mm"
        placeholderText="DD.MM.YYYY"
        locale={locale}
        renderCustomHeader={(props) => <DatePickerHeader {...props} />}
        // @ts-ignore
        customTimeInput={<DatePickerTimeSelect onChangeDate={datepickerProps.onChange} />}
        {...(datepickerProps as DatePickerProps)}
      />

      {isDefined(prefix) && (
        <div className={styles.prefix} ref={prefixRef}>
          {prefix}
        </div>
      )}

      {isDefined(suffix) && (
        <div className={styles.suffix} ref={suffixRef}>
          {suffix}
        </div>
      )}
    </div>
  );
};
